// @flow

import * as React from 'react';

import { ShoppingCart, Check, X } from 'lucide-react';

import { useTranslator } from '@gandi/translate';
import { Button } from '@gandi/ui';

import { useDelayedProps } from 'src/hooks/useDelayedProps';

type Props = {
  addedToCart?: boolean,
  optimistic?: boolean,
  isCartActionnable?: boolean,
  onClick: (evt: SyntheticMouseEvent<>) => Promise<void>,
};

function getKind({
  addedToCart,
  optimistic,
}: {
  addedToCart?: boolean,
  optimistic?: boolean,
}): 'ghost' | 'outline' | void {
  if (optimistic) return 'ghost';
  if (addedToCart) return undefined;
  return 'outline';
}

function CartButton({
  addedToCart,
  isCartActionnable,
  onClick,
  optimistic,
}: Props) {
  const { __ } = useTranslator();

  const added = addedToCart && !optimistic;

  const { label, Icon, intent } = useDelayedProps(
    { label: __('Added'), Icon: Check, intent: 'success' },
    { label: __('Remove'), Icon: X, intent: undefined },
    added,
  );
  const CurrentIcon = added ? Icon : ShoppingCart;
  const currentLabel = added ? label : __('Add to cart');

  return (
    <Button
      onClick={isCartActionnable ? onClick : undefined}
      disabled={!isCartActionnable}
      kind={getKind({ addedToCart, optimistic })}
      intent={added ? intent : undefined}
      loading={optimistic}
      title={currentLabel}
    >
      <CurrentIcon size={16} role="img" aria-label={currentLabel} />
    </Button>
  );
}

export default (React.memo<Props>(CartButton): React.ComponentType<Props>);
